function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { of } from 'rxjs';
import { complement } from 'ramda';
import { HistoryRecord } from './audit-history';
import { updateConsent } from './consents';
import { collateTPPs } from './tpps';
import formatCurrency from './currency-formatter';
var GROUPING_NAMES = ['ACTIVE', 'EXPIRED', 'DECLINED', 'WITHDRAWN', 'DELETED', 'TPP_SUSPENDED', 'TPP_CLIENT_SUSPENDED', 'TPP_REVOKED', 'BLOCKED'];
var CONSENT_STATUS_TO_HISTORY_LABEL = {
  ACCEPTED: 'Created',
  AUTHORIZED: 'Connected since',
  DECLINED: 'Declined',
  DELETED: 'Deleted',
  BLOCKED: 'Request blocked by bank',
  WITHDRAWN: 'Cancelled'
};
var GROUPINGS = GROUPING_NAMES.reduce(function (groupings, grouping) {
  return Object.assign({}, groupings, _defineProperty({}, grouping, grouping));
}, {});
var STATE_NAMES = [].concat(GROUPING_NAMES, ['AUTHORIZED', 'ACCEPTED', 'FAILED', 'REVOKED', 'SUSPENDED']);
var STATES = STATE_NAMES.reduce(function (states, state) {
  return Object.assign({}, states, _defineProperty({}, state, state));
}, {});
export var isActive = function isActive(vrp) {
  return vrp.get('grouping') === GROUPINGS.ACTIVE;
};
var isActiveTpp = function isActiveTpp(tpp) {
  return tpp.get('consents').some(isActive);
};
var updateVrpGeneratedState = function updateVrpGeneratedState(state) {
  var vrps = state.getIn(['vrp', 'index']).valueSeq().sort(function (a, b) {
    return a.createdAt - b.createdAt;
  });
  var tpps = collateTPPs(vrps);
  var activeTpps = tpps.filter(isActiveTpp);
  var inactiveTpps = tpps.filter(complement(isActiveTpp));
  return state.setIn(['vrp', 'tpps'], tpps).setIn(['vrp', 'activeTpps'], activeTpps).setIn(['vrp', 'inactiveTpps'], inactiveTpps);
};
var updateVrpGeneratedState$ = of(updateVrpGeneratedState);
function updateVrpHistoryState(consentId, consentAudits) {
  return function (state) {
    return state.updateIn(['vrp', 'index', consentId], function (vrp) {
      return vrp.set('history', getHistory(vrp, consentAudits));
    }).update(updateVrpGeneratedState);
  };
}
function updateVrpActivitiesState(consentId) {
  var activities = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return function (state) {
    return state.updateIn(['vrp', 'index', consentId], function (vrp) {
      return vrp.set('activities', getActivities(vrp, activities));
    }).update(updateVrpGeneratedState);
  };
}
function toHistoryRecords(consentAudit) {
  return new HistoryRecord({
    date: consentAudit.changeAt,
    status: consentAudit.status,
    label: CONSENT_STATUS_TO_HISTORY_LABEL[consentAudit.status] || '-'
  });
}
export function getHistoryForCurrentUser(vrp) {
  var isStaffMember = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return isStaffMember ? vrp.history : vrp.history.filter(function (_ref) {
    var status = _ref.status;
    return status !== 'ACCEPTED';
  });
}
function getHistory(vrp, consentAudits) {
  var history = consentAudits.map(toHistoryRecords);
  switch (vrp.get('grouping')) {
    case STATES.TPP_REVOKED:
    case STATES.REVOKED:
      return history.push(new HistoryRecord({
        date: null,
        label: 'Access revoked'
      })).reverse();
    case STATES.SUSPENDED:
    case STATES.TPP_CLIENT_SUSPENDED:
    case STATES.TPP_SUSPENDED:
      return history.push(new HistoryRecord({
        date: null,
        label: 'Access suspended'
      })).reverse();
    case STATES.EXPIRED:
      return history.push(new HistoryRecord({
        date: vrp.get('expiresAt'),
        label: 'Access expired'
      })).reverse();
    default:
      return history.reverse();
  }
}
function getActivities(vrp, activities) {
  return activities.map(toActivityRecord).reverse();
}
function toActivityRecord(activity) {
  var amount = formatCurrency(activity.getIn(['payload', 'currency']), activity.getIn(['payload', 'amount']));
  var reference = activity.getIn(['payload', 'paymentInformation', 'endToEndIdentification']);
  return new HistoryRecord({
    date: activity.get('createdAt'),
    status: activity.get('status'),
    label: [amount, reference].filter(Boolean).join(' | Reference: ')
  });
}
var updateVrpStateAfterLoad = function updateVrpStateAfterLoad(updatedVrp, consentAudits) {
  return function (state) {
    return state.updateIn(['vrp', 'index', updatedVrp.get('id')], function (vrp) {
      return updateConsent(vrp, updatedVrp).update(function (vrp) {
        return vrp.get('history').size > 0 ? vrp.set('history', getHistory(vrp, consentAudits)) : vrp;
      });
    }).update(updateVrpGeneratedState);
  };
};
function vrpDescriptiveDate(vrp) {
  var _vrp$toJS = vrp.toJS(),
    modifiedAt = _vrp$toJS.modifiedAt,
    expiresAt = _vrp$toJS.expiresAt,
    grouping = _vrp$toJS.grouping,
    createdAt = _vrp$toJS.createdAt;
  switch (grouping) {
    case GROUPINGS.DECLINED:
      return {
        label: 'Declined',
        date: modifiedAt,
        createdAt: createdAt
      };
    case GROUPINGS.DELETED:
      return {
        label: 'Deleted',
        date: modifiedAt,
        createdAt: createdAt
      };
    case GROUPINGS.WITHDRAWN:
      return {
        label: 'Cancelled',
        date: modifiedAt,
        createdAt: createdAt
      };
    case GROUPINGS.EXPIRED:
      return {
        label: 'Expired',
        date: expiresAt,
        createdAt: createdAt
      };
    case GROUPINGS.BLOCKED:
      return {
        label: 'Blocked',
        date: modifiedAt,
        createdAt: createdAt
      };
    case GROUPINGS.TPP_REVOKED:
      return {
        label: 'Revoked',
        date: '',
        createdAt: createdAt
      };
    case GROUPINGS.TPP_SUSPENDED:
    case GROUPINGS.TPP_CLIENT_SUSPENDED:
      return {
        label: 'Suspended',
        date: '',
        createdAt: createdAt
      };
    default:
      return {
        // Handle non-expiring consent
        label: expiresAt === null ? 'Ongoing' : 'Expires',
        date: expiresAt,
        createdAt: createdAt
      };
  }
}
function hasMerchantName(consent) {
  return consent.getIn(['payload', 'merchantName']);
}
function getClientAndMerchantText(consent, tpp) {
  var merchantName = hasMerchantName(consent);
  return merchantName ? "".concat(tpp.clientDisplayName, " and ").concat(merchantName) : "".concat(tpp.clientDisplayName);
}
var processVrpConsents = function processVrpConsents(consents) {
  return consents;
};
export { GROUPINGS, STATES, processVrpConsents, updateVrpGeneratedState$, updateVrpStateAfterLoad, updateVrpHistoryState, updateVrpActivitiesState, vrpDescriptiveDate, hasMerchantName, getClientAndMerchantText };